import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from './hooks/useAuthContext';

// Layout, Pages & Components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import FlashNews from './components/FlashNews';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import VillageHistory from './pages/VillageHistory';
import GovtSchemes from './pages/GovtSchemes';
import AllVillagers from './pages/AllVillagers';
import ExServicemen from './pages/ExServicemen';
import FormFilling from './pages/FormFilling';
import StatusCheck from './pages/StatusCheck';
import AdminDashboard from './admin/AdminDashboard';
import VerificationPage from './pages/VerificationPage';
import CertificatePage from './pages/CertificatePage';
import ManualCertificate from './pages/ManualCertificate';
import Downloads from './pages/Downloads'; // Import Downloads
import Chatbot from './components/Chatbot';

// A component to handle conditional layout rendering
const MainLayout = ({ children }) => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Paths where the main layout should be hidden
  const hideLayoutPaths = [
    '/admin',
    '/login',
    '/signup',
    '/manual-certificate'
  ];
  const shouldHideLayout = hideLayoutPaths.some(path => location.pathname.startsWith(path));

  return (
    <>
      {!shouldHideLayout && <Header onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />}
      {!shouldHideLayout && <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />}
      <div className={`flex flex-col flex-1 ${!shouldHideLayout ? 'pt-16' : ''}`}>
        {!shouldHideLayout && <FlashNews />}
        <main className={`flex-grow w-full ${!shouldHideLayout ? 'max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8' : ''}`}>
          {children}
        </main>
        {!shouldHideLayout && <Chatbot />}
        {!shouldHideLayout && <Footer />}
      </div>
    </>
  );
};

function App() {
  const { user, authIsReady } = useAuthContext();

  if (!authIsReady) {
    return <div className="flex justify-center items-center h-screen"><p>Loading Digital Sikhera...</p></div>;
  }

  return (
    <div className="App bg-slate-50 min-h-screen flex flex-col font-sans">
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <MainLayout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={!user ? <Login /> : <Navigate to="/admin" />} />
            <Route path="/signup" element={!user ? <Signup /> : <Navigate to="/" />} />
            <Route path="/history" element={<VillageHistory />} />
            <Route path="/yojna" element={<GovtSchemes />} />
            <Route path="/villagers" element={<AllVillagers />} />
            <Route path="/ex-servicemen" element={<ExServicemen />} />
            <Route path="/form-filling" element={<FormFilling />} />
            <Route path="/status-check" element={<StatusCheck />} />
            <Route path="/downloads" element={<Downloads />} /> {/* Downloads route */}
            <Route path="/verify/:serialNumber" element={<VerificationPage />} />
            <Route path="/certificate/:serialNumber" element={<CertificatePage />} />
            <Route path="/manual-certificate" element={<ManualCertificate />} />
            
            <Route 
              path="/admin"
              element={user && user.role === 'admin' ? <AdminDashboard /> : <Navigate to="/login" />}
            />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </MainLayout>
      </BrowserRouter>
    </div>
  );
}

export default App;
